import { useState, useEffect } from 'react'
import styled from 'styled-components'
import { api } from '../../../services/api'
import { realCellText, realStatusColor, realStatusLabel, sumReal, sumRealTotal } from '../../../utils/billingRedmine'
import type { RedmineRowFields, RedmineTableFields } from '../../../utils/billingRedmine'
import { Spinner, LoadingContainer } from '../../../components/shared/StyledComponents'

const Card = styled.div`
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  overflow-x: auto;
`

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.md};
`

const CardTitle = styled.h3`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSizes.lg};
  font-weight: 600;
`

const Controls = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  align-items: center;
`

const Select = styled.select`
  padding: ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: 600;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;

  ${({ $variant, theme }) =>
        $variant === 'secondary'
            ? `
        background: white;
        color: ${theme.colors.primary};
        border: 1px solid ${theme.colors.primary};
        &:hover { background: ${theme.colors.primaryLight}; }
      `
            : `
        background: ${theme.colors.primary};
        color: white;
        &:hover { opacity: 0.9; }
      `
    }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const TableWrapper = styled.div`
  overflow-x: auto;
  max-width: 100%;
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${({ theme }) => theme.fontSizes.xs};
  min-width: 800px;
`

const Thead = styled.thead`
  background: ${({ theme }) => theme.colors.secondary};
  position: sticky;
  top: 0;
  z-index: 10;
`

// The 'Proyecto' column is pinned at left: USER_COL_WIDTH, so the 'Usuario' column
// must be exactly that wide. It used to size to its content (~112px), and the pinned
// column then slid over the first day column, hiding its header and hours.
const USER_COL_WIDTH = 150
const userColumn = `
    width: ${USER_COL_WIDTH}px;
    min-width: ${USER_COL_WIDTH}px;
    max-width: ${USER_COL_WIDTH}px;
    box-sizing: border-box;
    overflow: hidden;
    text-overflow: ellipsis;
`

const Th = styled.th`
  padding: ${({ theme }) => theme.spacing.sm};
  text-align: left;
  font-weight: 600;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.secondary};
  white-space: nowrap;

  &:first-child {
    position: sticky;
    left: 0;
    z-index: 11;
    ${userColumn}
  }

  &:nth-child(2) {
    position: sticky;
    left: ${USER_COL_WIDTH}px;
    z-index: 11;
  }
`

const Tbody = styled.tbody``

const Tr = styled.tr<{ $isDefault?: boolean }>`
  background: ${({ $isDefault }) => $isDefault ? '#FFF8E1' : 'white'};

  &:hover {
    background: ${({ theme }) => theme.colors.primaryLight};
  }
`

const Td = styled.td`
  padding: ${({ theme }) => theme.spacing.xs};
  border: 1px solid ${({ theme }) => theme.colors.border};
  text-align: center;
  white-space: nowrap;

  &:first-child {
    position: sticky;
    left: 0;
    background: inherit;
    font-weight: 500;
    text-align: left;
    z-index: 9;
    ${userColumn}
  }

  &:nth-child(2) {
    position: sticky;
    left: ${USER_COL_WIDTH}px;
    background: inherit;
    text-align: left;
    z-index: 9;
  }
`

const ProjectLabel = styled.span<{ $isDefault?: boolean }>`
  color: ${({ $isDefault, theme }) => $isDefault ? '#F57C00' : theme.colors.text};
  font-style: ${({ $isDefault }) => $isDefault ? 'italic' : 'normal'};
`

const RealHours = styled.div<{ $status?: string | null }>`
  font-size: 0.68rem;
  line-height: 1.1;
  color: ${({ $status }) => realStatusColor($status as any)};
`

const RedmineTag = styled.span`
  display: inline-block;
  margin-left: 4px;
  padding: 0 5px;
  border-radius: 8px;
  font-size: 0.62rem;
  font-weight: 700;
  background: #E3F2FD;
  color: #1565C0;
  vertical-align: middle;
`

const RedmineNote = styled.div`
  font-size: 0.75rem;
  color: #555;
  margin: 6px 0 10px;
  padding: 6px 10px;
  border-left: 3px solid #90CAF9;
  background: #F5F9FF;
  border-radius: 4px;
`

const HoursCell = styled.div<{ $hours: number }>`
  color: ${({ $hours, theme }) =>
        $hours === 0 ? theme.colors.textMuted :
            $hours < 4 ? '#0288D1' :
                $hours < 7 ? '#388E3C' :
                    '#1976D2'
    };
  font-weight: ${({ $hours }) => $hours > 0 ? 600 : 400};
`

const TotalCell = styled.div`
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
`

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xl};
  color: ${({ theme }) => theme.colors.textMuted};
`

const Legend = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textMuted};
  flex-wrap: wrap;
`

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
`

const LegendColor = styled.div<{ $color: string }>`
  width: 12px;
  height: 12px;
  background: ${({ $color }) => $color};
  border-radius: 2px;
`

interface ProjectAllocationRow extends RedmineRowFields {
    user_id: number
    user_name: string
    opportunity_id?: number
    project_name: string
    redmine_code?: string
    daily_hours: { [date: string]: number }
    total_hours: number
    capacity_pct?: number
    is_default_project: boolean
}

interface MonthlyAllocationTable extends RedmineTableFields {
    year: number
    month: number
    working_days: string[]
    allocations: ProjectAllocationRow[]
}

export const BillingAllocationTable = () => {
    const [loading, setLoading] = useState(false)
    const [tableData, setTableData] = useState<MonthlyAllocationTable | null>(null)
    const [error, setError] = useState<string | null>(null)

    const currentDate = new Date()
    const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear())
    const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1)

    useEffect(() => {
        fetchAllocations()
    }, [selectedYear, selectedMonth])

    const fetchAllocations = async () => {
        try {
            setLoading(true)
            console.log(`[BillingAllocationTable] Fetching allocations for ${selectedYear}-${selectedMonth}`)
            const response = await api.billing.getAllocations(selectedYear, selectedMonth)
            console.log('[BillingAllocationTable] API Response:', response.data)
            setTableData(response.data)
            setError(null)
        } catch (err: any) {
            console.error('[BillingAllocationTable] Error fetching allocations:', err)
            console.error('[BillingAllocationTable] Error details:', err.response?.data)
            if (err.response?.status === 403) {
                setError('⚠️ Acceso denegado. Solo administradores pueden ver esta tabla.')
            } else {
                setError(err.response?.data?.detail || 'Error al cargar tabla de imputación')
            }
        } finally {
            setLoading(false)
        }
    }

    const formatDate = (dateStr: string): string => {
        const date = new Date(dateStr)
        const day = date.getDate()
        const weekday = date.toLocaleDateString('es-ES', { weekday: 'short' })
        return `${day} ${weekday}`
    }

    const monthNames = [
        'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ]

    const years = Array.from({ length: 3 }, (_, i) => currentDate.getFullYear() - 1 + i)

    if (loading) {
        return (
            <Card>
                <LoadingContainer>
                    <Spinner />
                    <p>Cargando tabla de imputación...</p>
                </LoadingContainer>
            </Card>
        )
    }

    return (
        <Card>
            <CardHeader>
                <CardTitle>📅 Tabla de Imputación</CardTitle>
                <Controls>
                    <Select
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(Number(e.target.value))}
                    >
                        {monthNames.map((name, index) => (
                            <option key={index} value={index + 1}>{name}</option>
                        ))}
                    </Select>
                    <Select
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(Number(e.target.value))}
                    >
                        {years.map(year => (
                            <option key={year} value={year}>{year}</option>
                        ))}
                    </Select>
                    <Button onClick={fetchAllocations}>🔄 Actualizar</Button>
                </Controls>
            </CardHeader>

            {error && <EmptyState>{error}</EmptyState>}

            {!error && tableData && (
                <>
                    {tableData.allocations.length === 0 ? (
                        <EmptyState>
                            <p>No hay asignaciones de proyecto para {monthNames[selectedMonth - 1]} {selectedYear}.</p>
                            <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '8px' }}>
                                💡 Los usuarios deben estar asignados a proyectos con fechas que incluyan este mes.
                                <br />Días laborables en el mes: {tableData.working_days.length}
                            </p>
                        </EmptyState>
                    ) : (
                        <>
                            {tableData && (
                                <RedmineNote>
                                    {tableData.redmine_available ? (
                                        <>
                                            🔗 Debajo de cada hora planificada aparece, en pequeño, la hora <strong>real imputada en Redmine</strong>
                                            {tableData.as_of ? ` (hasta ${tableData.as_of})` : ''}: verde al día, naranja por debajo, rojo por encima, azul horas en Redmine sin planificar.
                                            {tableData.unmatched_users && tableData.unmatched_users.length > 0 && (
                                                <> Sin usuario Redmine identificado: {tableData.unmatched_users.join(', ')}.</>
                                            )}
                                        </>
                                    ) : (
                                        <>⚠️ Horas reales de Redmine no disponibles{tableData.redmine_message ? `: ${tableData.redmine_message}` : ''}.</>
                                    )}
                                </RedmineNote>
                            )}
                            <TableWrapper>
                                <Table>
                                    <Thead>
                                        <tr>
                                            <Th>Usuario</Th>
                                            <Th>Proyecto</Th>
                                            {tableData.working_days.map(day => (
                                                <Th key={day}>{formatDate(day)}</Th>
                                            ))}
                                            <Th>Total</Th>
                                        </tr>
                                    </Thead>
                                    <Tbody>
                                        {tableData.allocations.map((row, index) => (
                                            <Tr key={index} $isDefault={row.is_default_project}>
                                                <Td title={row.user_name}>{row.user_name}</Td>
                                                <Td>
                                                    <ProjectLabel $isDefault={row.is_default_project}>
                                                        {row.project_name}
                                                        {row.is_real_only && <RedmineTag>Redmine</RedmineTag>}
                                                        {row.capacity_pct && ` (${row.capacity_pct}%)`}
                                                    </ProjectLabel>
                                                </Td>
                                                {tableData.working_days.map(day => (
                                                    <Td key={day}>
                                                        <HoursCell $hours={row.daily_hours[day] || 0}>
                                                            {row.daily_hours[day] ? row.daily_hours[day].toFixed(2) : '-'}
                                                        </HoursCell>
                                                        {realCellText(row, day, tableData.as_of) !== '' && (
                                                            <RealHours $status={row.real_status} title={realStatusLabel(row.real_status)}>{realCellText(row, day, tableData.as_of)}</RealHours>
                                                        )}
                                                    </Td>
                                                ))}
                                                <Td>
                                                    <TotalCell>{row.total_hours.toFixed(1)}h</TotalCell>
                                                    {row.real_available && row.total_real != null && (
                                                        <RealHours $status={row.real_status} title={realStatusLabel(row.real_status)}>{row.total_real.toFixed(1)}h Redmine</RealHours>
                                                    )}
                                                </Td>
                                            </Tr>
                                        ))}
                                    </Tbody>
                                </Table>
                            </TableWrapper>

                            <Legend>
                                <LegendItem>
                                    <LegendColor $color="#FFF8E1" />
                                    <span>Proyecto por defecto (capacidad no asignada)</span>
                                </LegendItem>
                                <LegendItem>
                                    <span>💡 Las horas se distribuyen según el porcentaje de capacidad asignado</span>
                                </LegendItem>
                            </Legend>
                        </>
                    )}
                </>
            )}
        </Card>
    )
}
